import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowDownCircle,
  ArrowUpCircle,
  Briefcase,
  Calendar,
  Download,
  FileText,
  Filter,
  Printer,
  RefreshCw,
  Search,
  ShieldCheck,
  Users,
  Wallet,
} from 'lucide-react';
import ApiURL from '../Api/Api';
import { fetchJson } from '../utils/http';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import { SidebarContext } from './SidebarContext';
import TransactionProofImage from './TransactionProofImage';

const tabs = [
  { id: 'overview', label: 'Overview' },
  { id: 'users', label: 'Users' },
  { id: 'ledger', label: 'Ledger' },
  { id: 'requests', label: 'Requests' },
  { id: 'projects', label: 'Projects' },
  { id: 'heads', label: 'Heads' },
  { id: 'accounting', label: 'Accounting' },
  { id: 'reconciliation', label: 'Reconciliation' },
  { id: 'audit', label: 'Audit' },
];

const emptyReport = {
  scope: {},
  overview: {},
  request_summary: {},
  users: [],
  site_summary: [],
  head_summary: [],
  project_summary: [],
  ledger_rows: [],
  journal_entries: [],
  trial_balance: {},
  profit_and_loss: {},
  balance_sheet: {},
  audit_trail: [],
  reconciliation: {},
  requests: [],
  site_options: [],
  project_options: [],
  generated_at: '',
};

const money = (value) =>
  `Rs. ${Number(value || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const shortMoney = (value) =>
  `Rs. ${Number(value || 0).toLocaleString('en-IN', {
    maximumFractionDigits: 0,
  })}`;

const cleanText = (value, fallback = '-') => {
  const text = String(value ?? '').trim();
  return text || fallback;
};

const titleCase = (value) =>
  cleanText(value, '')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase()) || '-';

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(String(value).replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const statusClass = (status) => {
  const key = String(status || '').toLowerCase();
  if (key === 'approved' || key === 'received' || key === 'request approved') {
    return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  }
  if (key === 'pending' || key === 'inprogress' || key === 'save') {
    return 'bg-amber-50 text-amber-700 border-amber-200';
  }
  if (key === 'rejected' || key === 'return') {
    return 'bg-rose-50 text-rose-700 border-rose-200';
  }
  return 'bg-slate-50 text-slate-700 border-slate-200';
};

const downloadCsv = (filename, rows) => {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const escapeCell = (value) => {
    const text = String(value ?? '');
    return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };
  const csv = [
    headers.join(','),
    ...rows.map((row) => headers.map((header) => escapeCell(row[header])).join(',')),
  ].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

const Metric = ({ icon: Icon, label, value, tone = 'slate', caption }) => {
  const tones = {
    slate: 'border-slate-200 bg-white text-slate-900',
    green: 'border-emerald-200 bg-emerald-50 text-emerald-900',
    blue: 'border-sky-200 bg-sky-50 text-sky-900',
    amber: 'border-amber-200 bg-amber-50 text-amber-900',
    rose: 'border-rose-200 bg-rose-50 text-rose-900',
  };

  return (
    <div className={`rounded-lg border p-4 shadow-sm ${tones[tone] || tones.slate}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
          <p className="mt-2 text-xl font-bold leading-tight text-slate-950">{value}</p>
          {caption ? <p className="mt-1 text-xs text-slate-500">{caption}</p> : null}
        </div>
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white/80 text-slate-700 shadow-sm">
          <Icon size={20} />
        </div>
      </div>
    </div>
  );
};

const SectionTitle = ({ title, subtitle, right }) => (
  <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
    <div>
      <h2 className="text-lg font-bold text-slate-950">{title}</h2>
      {subtitle ? <p className="mt-1 text-sm text-slate-500">{subtitle}</p> : null}
    </div>
    {right}
  </div>
);

const LedgerIntelligenceReport = () => {
  const { isSidebarOpen = true } = useContext(SidebarContext) || {};
  const userId = localStorage.getItem('id') || '';
  const role = localStorage.getItem('role') || '';
  const [activeTab, setActiveTab] = useState('overview');
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    site: '',
    project: '',
    status: '',
    search: '',
  });
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [report, setReport] = useState(emptyReport);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [drillFilter, setDrillFilter] = useState(null);

  const canView = role === 'company_owner' || role === 'admin';

  const endpoint = useMemo(() => {
    const params = new URLSearchParams({
      user_id: userId,
      role,
    });
    Object.entries(appliedFilters).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    return `${ApiURL}/getLedgerIntelligenceReport.php?${params.toString()}`;
  }, [appliedFilters, role, userId]);

  const loadReport = useCallback(async () => {
    if (!canView || !userId) {
      setError('Access denied. Ledger reports are available for company owners and admins only.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const data = await fetchJson(endpoint);
      if (data.status !== 'success') {
        throw new Error(data.message || 'Failed to load ledger report');
      }
      setReport({ ...emptyReport, ...data });
    } catch (err) {
      setError(err.message || 'Failed to load ledger report');
    } finally {
      setLoading(false);
    }
  }, [canView, endpoint, userId]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const overview = report.overview || {};
  const requestSummary = report.request_summary || {};
  const rows = report.ledger_rows || [];
  const requests = report.requests || [];
  const trialBalance = report.trial_balance || {};
  const profitAndLoss = report.profit_and_loss || {};
  const balanceSheet = report.balance_sheet || {};
  const reconciliation = report.reconciliation || {};
  const auditTrail = report.audit_trail || [];
  const journalEntries = report.journal_entries || [];

  const visibleRows = useMemo(() => {
    if (!drillFilter) return rows;
    if (drillFilter.type === 'site') {
      return rows.filter((row) => cleanText(row.site, 'Unassigned') === drillFilter.value);
    }
    if (drillFilter.type === 'head') {
      return rows.filter(
        (row) => `${cleanText(row.main_head, 'Unassigned')} / ${cleanText(row.sub_head, 'Unassigned')}` === drillFilter.value
      );
    }
    if (drillFilter.type === 'project') {
      return rows.filter((row) => cleanText(row.project_name, 'Unassigned') === drillFilter.value);
    }
    if (drillFilter.type === 'classification') {
      return rows.filter((row) => row.classification === drillFilter.value);
    }
    return rows;
  }, [drillFilter, rows]);

  const openDrill = (filter) => {
    setDrillFilter(filter);
    setActiveTab('ledger');
  };

  const companySpending = Number(
    overview.company_spending ??
      (Number(overview.owner_spending || 0) + Number(overview.admin_spending || 0) + Number(overview.worker_spending || 0))
  );

  const kpis = [
    { label: 'Opening Ledger Balance', value: money(overview.opening_balance), icon: Wallet, tone: 'slate' },
    { label: 'Total Credit', value: money(overview.total_credit ?? overview.period_credit), icon: ArrowUpCircle, tone: 'green' },
    { label: 'Company Spending', value: money(companySpending), icon: ArrowDownCircle, tone: 'amber' },
    ...(role === 'admin' ? [] : [{ label: 'Allocated To Admins', value: money(overview.allocated_to_admin), icon: Briefcase, tone: 'blue' }]),
    { label: 'Allocated To Workers', value: money(overview.allocated_to_worker), icon: Users, tone: 'blue' },
    { label: 'Admin Spending', value: money(overview.admin_spending), icon: ArrowDownCircle, tone: 'amber' },
    { label: 'Worker Spending', value: money(overview.worker_spending), icon: ArrowDownCircle, tone: 'amber' },
    { label: 'Returns Paid', value: money((overview.admin_returns || 0) + (overview.worker_returns || 0)), icon: RefreshCw, tone: 'rose' },
    { label: 'Closing Ledger Balance', value: money(overview.closing_balance), icon: FileText, tone: 'slate' },
  ];

  const warningItems = [
    Number(overview.negative_balance_count || 0) > 0
      ? `${overview.negative_balance_count} user ledger(s) are closing negative`
      : '',
    Math.abs(Number(overview.reconciliation_difference || 0)) > 0.01
      ? `Reconciliation difference: ${money(overview.reconciliation_difference)}`
      : '',
  ].filter(Boolean);

  const requestRows = [
    ['Admin fund requests', requestSummary.admin_requests],
    ['Worker fund requests', requestSummary.worker_requests],
    ['Worker return requests', requestSummary.worker_returns],
    ['Admin return requests', requestSummary.admin_returns],
  ];

  const exportLedger = () => {
    const exportRows = rows.map((row) => ({
      Date: row.transaction_date,
      Created: row.created_at,
      User: row.user_name,
      Role: row.role,
      TransactionNo: row.transaction_no,
      Type: row.amount_type,
      Credit: row.credit_amount,
      Debit: row.debit_amount,
      RunningBalance: row.running_balance,
      Party: row.particuler_to,
      PartyRole: row.recipient_role,
      Site: row.site,
      Project: row.project_name,
      MainHead: row.main_head,
      SubHead: row.sub_head,
      Status: row.form_status,
      Classification: row.classification,
      Remarks: row.remarks,
    }));
    downloadCsv('ledger-intelligence-report.csv', exportRows);
  };

  const applyFilters = (event) => {
    event.preventDefault();
    setAppliedFilters(filters);
  };

  const resetFilters = () => {
    const reset = { start_date: '', end_date: '', site: '', project: '', status: '', search: '' };
    setFilters(reset);
    setAppliedFilters(reset);
  };

  return (
    <div className="min-h-screen bg-slate-100 pt-16 font-sans text-slate-800">
      <Navbar />
      <div className="flex min-h-[calc(100vh-4rem)]">
        <Sidebar />
        <main
          className={`flex-1 p-3 transition-all duration-200 sm:p-5 lg:p-6 ${
            isSidebarOpen ? 'md:ml-72' : 'md:ml-0'
          }`}
        >
          <div className="mx-auto max-w-[1600px]">
            <div className="mb-5 flex flex-col gap-4 border-b border-slate-200 pb-5 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Ledger Intelligence</p>
                <h1 className="mt-1 text-2xl font-bold text-slate-950 sm:text-3xl">Complete Ledger Report</h1>
                <p className="mt-2 text-sm text-slate-500">
                  {cleanText(report.scope?.company_name, 'Company ledger')} - generated {formatDate(report.generated_at)}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={loadReport}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
                  title="Refresh report"
                >
                  <RefreshCw size={16} />
                  Refresh
                </button>
                <button
                  type="button"
                  onClick={exportLedger}
                  disabled={!rows.length}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  title="Export ledger rows"
                >
                  <Download size={16} />
                  CSV
                </button>
                <button
                  type="button"
                  onClick={() => window.print()}
                  className="inline-flex items-center gap-2 rounded-lg bg-slate-950 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-slate-800"
                  title="Print report"
                >
                  <Printer size={16} />
                  Print
                </button>
              </div>
            </div>

            <form onSubmit={applyFilters} className="mb-5 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-6">
                <label className="block">
                  <span className="mb-1 flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <Calendar size={14} /> From
                  </span>
                  <input
                    type="date"
                    value={filters.start_date}
                    onChange={(event) => setFilters((prev) => ({ ...prev, start_date: event.target.value }))}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100"
                  />
                </label>
                <label className="block">
                  <span className="mb-1 flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <Calendar size={14} /> To
                  </span>
                  <input
                    type="date"
                    value={filters.end_date}
                    onChange={(event) => setFilters((prev) => ({ ...prev, end_date: event.target.value }))}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100"
                  />
                </label>
                <label className="block">
                  <span className="mb-1 flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <Filter size={14} /> Site
                  </span>
                  <select
                    value={filters.site}
                    onChange={(event) => setFilters((prev) => ({ ...prev, site: event.target.value }))}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100"
                  >
                    <option value="">All sites</option>
                    {(report.site_options || []).map((site) => (
                      <option key={site} value={site}>
                        {site}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="mb-1 flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <Filter size={14} /> Status
                  </span>
                  <select
                    value={filters.status}
                    onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100"
                  >
                    <option value="">All statuses</option>
                    <option value="SUBMIT">Submit</option>
                    <option value="RECEIVED">Received</option>
                    <option value="Request approved">Request approved</option>
                    <option value="return">Return</option>
                  </select>
                </label>
                <label className="block">
                  <span className="mb-1 flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <Briefcase size={14} /> Project
                  </span>
                  <select
                    value={filters.project}
                    onChange={(event) => setFilters((prev) => ({ ...prev, project: event.target.value }))}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100"
                  >
                    <option value="">All projects</option>
                    {(report.project_options || []).map((project) => (
                      <option key={project} value={project}>
                        {project}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="mb-1 flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <Search size={14} /> Search
                  </span>
                  <div className="flex gap-2">
                    <input
                      type="search"
                      value={filters.search}
                      onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
                      placeholder="User, party, head, remarks"
                      className="min-w-0 flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-100"
                    />
                    <button type="submit" className="rounded-lg bg-sky-700 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-800">
                      Apply
                    </button>
                    <button type="button" onClick={resetFilters} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700">
                      Reset
                    </button>
                  </div>
                </label>
              </div>
            </form>

            {error ? (
              <div className="mb-5 rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm font-medium text-rose-700">
                {error}
              </div>
            ) : null}

            {loading ? (
              <div className="rounded-lg border border-slate-200 bg-white p-10 text-center text-sm font-semibold text-slate-500 shadow-sm">
                Preparing ledger calculations...
              </div>
            ) : (
              <>
                {warningItems.length ? (
                  <div className="mb-5 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                    <div className="flex items-start gap-2">
                      <AlertTriangle size={18} className="mt-0.5 shrink-0" />
                      <div>
                        <p className="font-semibold">Review attention points</p>
                        <p className="mt-1">{warningItems.join(' | ')}</p>
                      </div>
                    </div>
                  </div>
                ) : null}

                <div className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  {kpis.map((item) => (
                    <Metric key={item.label} {...item} />
                  ))}
                </div>

                {(report.project_summary || []).length ? (
                  <section className="mb-5 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                    <SectionTitle
                      title="Project Dashboard"
                      subtitle="Project budget remaining is calculated from actual spend only. Admin and worker fund transfers are shown separately."
                    />
                    <ProjectComparisonChart
                      projects={report.project_summary || []}
                      onOpen={(project) => openDrill({ type: 'project', value: cleanText(project.project_name, 'Unassigned'), label: `project ${cleanText(project.project_name, 'Unassigned')}` })}
                    />
                    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                      {(report.project_summary || []).map((project) => (
                        <ProjectBudgetCard
                          key={project.project_name}
                          project={project}
                          onOpen={() => openDrill({ type: 'project', value: cleanText(project.project_name, 'Unassigned'), label: `project ${cleanText(project.project_name, 'Unassigned')}` })}
                        />
                      ))}
                    </div>
                  </section>
                ) : null}

                <div className="mb-5 flex gap-2 overflow-x-auto border-b border-slate-300">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setActiveTab(tab.id)}
                      className={`whitespace-nowrap border-b-2 px-4 py-3 text-sm font-semibold transition ${
                        activeTab === tab.id
                          ? 'border-sky-700 text-sky-800'
                          : 'border-transparent text-slate-500 hover:text-slate-900'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                {activeTab === 'overview' ? (
                  <div className="space-y-5">
                    <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                      <SectionTitle
                        title="Money Movement"
                        subtitle="Requested amounts are shown separately from actual ledger entries."
                      />
                      <div className="grid gap-3 lg:grid-cols-4">
                        <Metric label="Total Requested" value={money(requestSummary.total_requested)} icon={FileText} tone="slate" />
                        <Metric label="Pending Requests" value={money(requestSummary.total_pending)} icon={AlertTriangle} tone="amber" />
                        <Metric label="Approved Requests" value={money(requestSummary.total_approved)} icon={ArrowUpCircle} tone="green" />
                        <Metric label="Rejected Requests" value={money(requestSummary.total_rejected)} icon={ArrowDownCircle} tone="rose" />
                      </div>
                    </section>

                    <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                      <SectionTitle title="Request Register" subtitle="Fund and return requests by approval status." />
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead className="bg-slate-100 text-xs uppercase tracking-wide text-slate-600">
                            <tr>
                              <th className="px-3 py-3 text-left">Request Type</th>
                              <th className="px-3 py-3 text-right">Total</th>
                              <th className="px-3 py-3 text-right">Pending</th>
                              <th className="px-3 py-3 text-right">Approved</th>
                              <th className="px-3 py-3 text-right">Rejected</th>
                              <th className="px-3 py-3 text-right">Count</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {requestRows.map(([label, data = {}]) => (
                              <tr key={label} className="hover:bg-slate-50">
                                <td className="px-3 py-3 font-semibold text-slate-900">{label}</td>
                                <td className="px-3 py-3 text-right">{money(data.amount)}</td>
                                <td className="px-3 py-3 text-right text-amber-700">{money(data.pending_amount)}</td>
                                <td className="px-3 py-3 text-right text-emerald-700">{money(data.approved_amount)}</td>
                                <td className="px-3 py-3 text-right text-rose-700">{money(data.rejected_amount)}</td>
                                <td className="px-3 py-3 text-right">{data.count || 0}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </section>
                  </div>
                ) : null}

                {activeTab === 'users' ? (
                  <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                    <SectionTitle title="User Ledger Balances" subtitle="Opening + credits - debits = closing, recalculated from ledger rows." />
                    <div className="overflow-x-auto">
                      <table className="min-w-[1200px] w-full text-sm">
                        <thead className="bg-slate-100 text-xs uppercase tracking-wide text-slate-600">
                          <tr>
                            <th className="px-3 py-3 text-left">User</th>
                            <th className="px-3 py-3 text-left">Role</th>
                            <th className="px-3 py-3 text-right">Opening</th>
                            <th className="px-3 py-3 text-right">Credit</th>
                            <th className="px-3 py-3 text-right">Debit</th>
                            <th className="px-3 py-3 text-right">Closing</th>
                            <th className="px-3 py-3 text-right">Allocated In</th>
                            <th className="px-3 py-3 text-right">Allocated Out</th>
                            <th className="px-3 py-3 text-right">Spending</th>
                            <th className="px-3 py-3 text-right">Requested</th>
                            <th className="px-3 py-3 text-right">Variance</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {(report.users || []).map((user) => (
                            <tr key={user.user_id} className={Number(user.closing_balance) < 0 ? 'bg-rose-50/70' : 'hover:bg-slate-50'}>
                              <td className="px-3 py-3">
                                <div className="font-semibold text-slate-950">{cleanText(user.user_name)}</div>
                                <div className="text-xs text-slate-500">{(user.assigned_sites || []).join(', ') || 'No site assigned'}</div>
                              </td>
                              <td className="px-3 py-3 capitalize">{titleCase(user.role)}</td>
                              <td className="px-3 py-3 text-right">{money(user.opening_balance)}</td>
                              <td className="px-3 py-3 text-right text-emerald-700">{money(user.credit)}</td>
                              <td className="px-3 py-3 text-right text-rose-700">{money(user.debit)}</td>
                              <td className="px-3 py-3 text-right font-bold">{money(user.closing_balance)}</td>
                              <td className="px-3 py-3 text-right">{money(user.allocated_in)}</td>
                              <td className="px-3 py-3 text-right">{money(user.allocated_out)}</td>
                              <td className="px-3 py-3 text-right">{money(user.spending)}</td>
                              <td className="px-3 py-3 text-right">{money(user.requested)}</td>
                              <td className="px-3 py-3 text-right">{money(user.variance)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                ) : null}

                {activeTab === 'ledger' ? (
                  <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                    <SectionTitle
                      title="Detailed Ledger"
                      subtitle={`${visibleRows.length} ledger entries shown${drillFilter ? ` for ${drillFilter.label}` : ''}.`}
                      right={<span className="text-sm font-semibold text-slate-500">{shortMoney(overview.period_credit)} credit / {shortMoney(overview.period_debit)} debit</span>}
                    />
                    {drillFilter ? (
                      <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-800">
                        <span>Drill-down active: <strong>{drillFilter.label}</strong></span>
                        <button type="button" onClick={() => setDrillFilter(null)} className="font-semibold hover:text-sky-950">
                          Clear drill-down
                        </button>
                      </div>
                    ) : null}
                    <div className="overflow-x-auto">
                      <table className="min-w-[1500px] w-full text-sm">
                        <thead className="bg-slate-100 text-xs uppercase tracking-wide text-slate-600">
                          <tr>
                            <th className="px-3 py-3 text-left">Date</th>
                            <th className="px-3 py-3 text-left">User</th>
                            <th className="px-3 py-3 text-left">Txn</th>
                            <th className="px-3 py-3 text-left">Party</th>
                            <th className="px-3 py-3 text-left">Site / Project / Head</th>
                            <th className="px-3 py-3 text-right">Credit</th>
                            <th className="px-3 py-3 text-right">Debit</th>
                            <th className="px-3 py-3 text-right">Balance</th>
                            <th className="px-3 py-3 text-left">Status</th>
                            <th className="px-3 py-3 text-left">Classification</th>
                            <th className="px-3 py-3 text-left">Remarks</th>
                            <th className="px-3 py-3 text-left">Proof</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {visibleRows.map((row) => (
                            <tr key={`${row.source_table}-${row.row_id}-${row.user_id}`} className="hover:bg-slate-50">
                              <td className="px-3 py-3 whitespace-nowrap">
                                <div className="font-medium text-slate-900">{cleanText(row.transaction_date)}</div>
                                <div className="text-xs text-slate-500">{formatDate(row.created_at)}</div>
                              </td>
                              <td className="px-3 py-3">
                                <div className="font-semibold text-slate-950">{cleanText(row.user_name)}</div>
                                <div className="text-xs capitalize text-slate-500">{titleCase(row.role)}</div>
                              </td>
                              <td className="px-3 py-3">#{row.transaction_no}</td>
                              <td className="px-3 py-3">
                                <div>{cleanText(row.particuler_to)}</div>
                                <div className="text-xs capitalize text-slate-500">{titleCase(row.recipient_role)}</div>
                              </td>
                              <td className="px-3 py-3">
                                <div>{cleanText(row.site)}</div>
                                <div className="text-xs text-slate-500">{cleanText(row.project_name)}</div>
                                <div className="text-xs text-slate-500">{cleanText(row.main_head)} / {cleanText(row.sub_head)}</div>
                              </td>
                              <td className="px-3 py-3 text-right text-emerald-700">{money(row.credit_amount)}</td>
                              <td className="px-3 py-3 text-right text-rose-700">{money(row.debit_amount)}</td>
                              <td className="px-3 py-3 text-right font-semibold">{money(row.running_balance)}</td>
                              <td className="px-3 py-3">
                                <span className={`inline-flex rounded-full border px-2 py-1 text-xs font-semibold ${statusClass(row.form_status)}`}>
                                  {titleCase(row.form_status)}
                                </span>
                              </td>
                              <td className="px-3 py-3">{titleCase(row.classification)}</td>
                              <td className="px-3 py-3 max-w-xs truncate" title={row.remarks}>{cleanText(row.remarks)}</td>
                              <td className="px-3 py-3"><TransactionProofImage path={row.attachment_path} /></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                ) : null}

                {activeTab === 'requests' ? (
                  <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                    <SectionTitle title="Request Details" subtitle={`${requests.length} request records after filters.`} />
                    <div className="overflow-x-auto">
                      <table className="min-w-[1100px] w-full text-sm">
                        <thead className="bg-slate-100 text-xs uppercase tracking-wide text-slate-600">
                          <tr>
                            <th className="px-3 py-3 text-left">Type</th>
                            <th className="px-3 py-3 text-left">From</th>
                            <th className="px-3 py-3 text-left">To</th>
                            <th className="px-3 py-3 text-right">Amount</th>
                            <th className="px-3 py-3 text-left">Site / Head</th>
                            <th className="px-3 py-3 text-left">Status</th>
                            <th className="px-3 py-3 text-left">Created</th>
                            <th className="px-3 py-3 text-left">Purpose</th>
                            <th className="px-3 py-3 text-left">Proof</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {requests.map((request) => (
                            <tr key={`${request.type}-${request.id}`} className="hover:bg-slate-50">
                              <td className="px-3 py-3 font-semibold">{titleCase(request.type)}</td>
                              <td className="px-3 py-3">{cleanText(request.from_name)}</td>
                              <td className="px-3 py-3">{cleanText(request.to_name)}</td>
                              <td className="px-3 py-3 text-right font-semibold">{money(request.amount)}</td>
                              <td className="px-3 py-3">
                                <div>{cleanText(request.site)}</div>
                                <div className="text-xs text-slate-500">{cleanText(request.main_head)} / {cleanText(request.sub_head)}</div>
                              </td>
                              <td className="px-3 py-3">
                                <span className={`inline-flex rounded-full border px-2 py-1 text-xs font-semibold ${statusClass(request.status)}`}>
                                  {titleCase(request.status)}
                                </span>
                              </td>
                              <td className="px-3 py-3">{formatDate(request.created_at)}</td>
                              <td className="px-3 py-3 max-w-xs truncate" title={request.purpose}>{cleanText(request.purpose)}</td>
                              <td className="px-3 py-3"><TransactionProofImage path={request.attachment_path} /></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                ) : null}

                {activeTab === 'heads' ? (
                  <div className="grid gap-5 xl:grid-cols-2">
                    <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                      <SectionTitle title="Site Analysis" subtitle="Credits, debits, spending, and allocations by site." />
                      <SummaryTable
                        rows={report.site_summary || []}
                        labelKey="site"
                        labelTitle="Site"
                        onDrill={(row) => openDrill({ type: 'site', value: cleanText(row.site, 'Unassigned'), label: `site ${cleanText(row.site, 'Unassigned')}` })}
                      />
                    </section>
                    <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                      <SectionTitle title="Head Analysis" subtitle="CA-style grouping by main head and sub head." />
                      <SummaryTable
                        rows={(report.head_summary || []).map((item) => ({
                          ...item,
                          label: `${cleanText(item.main_head)} / ${cleanText(item.sub_head)}`,
                        }))}
                        labelKey="label"
                        labelTitle="Head"
                        onDrill={(row) => openDrill({ type: 'head', value: row.label, label: `head ${row.label}` })}
                      />
                    </section>
                  </div>
                ) : null}

                {activeTab === 'projects' ? (
                  <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                    <SectionTitle
                      title="Project Budget Analysis"
                      subtitle="Actual spend counts expense rows only. Fund transfers between owner, admin, and worker are shown separately and not double-counted as project cost."
                    />
                    <ProjectSummaryTable
                      rows={report.project_summary || []}
                      onDrill={(row) => openDrill({ type: 'project', value: cleanText(row.project_name, 'Unassigned'), label: `project ${cleanText(row.project_name, 'Unassigned')}` })}
                    />
                  </section>
                ) : null}

                {activeTab === 'accounting' ? (
                  <div className="space-y-5">
                    <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                      <SectionTitle
                        title="Double Entry Journal"
                        subtitle="Inferred Dr/Cr vouchers generated from your existing ledger without double-counting mirrored rows."
                        right={<span className="text-sm font-semibold text-slate-500">{journalEntries.length} vouchers</span>}
                      />
                      <div className="overflow-x-auto">
                        <table className="min-w-[1300px] w-full text-sm">
                          <thead className="bg-slate-100 text-xs uppercase tracking-wide text-slate-600">
                            <tr>
                              <th className="px-3 py-3 text-left">Voucher</th>
                              <th className="px-3 py-3 text-left">Date</th>
                              <th className="px-3 py-3 text-left">Debit Account</th>
                              <th className="px-3 py-3 text-left">Credit Account</th>
                              <th className="px-3 py-3 text-right">Amount</th>
                              <th className="px-3 py-3 text-left">Classification</th>
                              <th className="px-3 py-3 text-left">Audit</th>
                              <th className="px-3 py-3 text-left">Narration</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {journalEntries.map((entry) => (
                              <tr key={entry.voucher_id} className="hover:bg-slate-50">
                                <td className="px-3 py-3 font-semibold">{entry.voucher_id}</td>
                                <td className="px-3 py-3">{cleanText(entry.transaction_date)}</td>
                                <td className="px-3 py-3 text-slate-950">{cleanText(entry.debit_account)}</td>
                                <td className="px-3 py-3 text-slate-950">{cleanText(entry.credit_account)}</td>
                                <td className="px-3 py-3 text-right font-semibold">{money(entry.amount)}</td>
                                <td className="px-3 py-3">{titleCase(entry.classification)}</td>
                                <td className="px-3 py-3">
                                  <div>{cleanText(entry.created_by)}</div>
                                  <div className="text-xs text-slate-500">{formatDate(entry.created_at)}</div>
                                </td>
                                <td className="px-3 py-3 max-w-sm truncate" title={entry.narration}>{cleanText(entry.narration)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </section>

                    <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                      <SectionTitle title="Trial Balance" subtitle="Debit and credit totals must match for accounting validation." />
                      <div className="mb-3 grid gap-3 md:grid-cols-3">
                        <Metric label="Total Debit" value={money(trialBalance.total_debit)} icon={ArrowDownCircle} tone="blue" />
                        <Metric label="Total Credit" value={money(trialBalance.total_credit)} icon={ArrowUpCircle} tone="green" />
                        <Metric label="Difference" value={money(trialBalance.difference)} icon={ShieldCheck} tone={trialBalance.is_balanced ? 'green' : 'rose'} />
                      </div>
                      <AccountingTable rows={trialBalance.accounts || []} />
                    </section>

                    <div className="grid gap-5 xl:grid-cols-2">
                      <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                        <SectionTitle title="Profit & Loss" subtitle="Expense view based on transaction heads." />
                        <StatementList rows={profitAndLoss.expenses || []} emptyLabel="No expenses found" />
                        <div className="mt-4 border-t border-slate-200 pt-3 text-right text-sm font-bold">
                          Net Profit / Loss: {money(profitAndLoss.net_profit)}
                        </div>
                      </section>
                      <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                        <SectionTitle title="Balance Sheet" subtitle="Assets are checked against liabilities plus equity." />
                        <div className="grid gap-4 md:grid-cols-2">
                          <div>
                            <h3 className="mb-2 text-sm font-bold text-slate-900">Assets</h3>
                            <StatementList rows={balanceSheet.assets || []} emptyLabel="No assets found" />
                          </div>
                          <div>
                            <h3 className="mb-2 text-sm font-bold text-slate-900">Equity & Liabilities</h3>
                            <StatementList rows={[...(balanceSheet.equity || []), ...(balanceSheet.liabilities || [])]} emptyLabel="No equity/liabilities found" />
                          </div>
                        </div>
                        <div className="mt-4 grid gap-2 border-t border-slate-200 pt-3 text-sm font-semibold sm:grid-cols-3">
                          <span>Assets: {money(balanceSheet.total_assets)}</span>
                          <span>L + E: {money(balanceSheet.liabilities_plus_equity)}</span>
                          <span className={Number(balanceSheet.difference || 0) === 0 ? 'text-emerald-700' : 'text-rose-700'}>
                            Difference: {money(balanceSheet.difference)}
                          </span>
                        </div>
                      </section>
                    </div>
                  </div>
                ) : null}

                {activeTab === 'reconciliation' ? (
                  <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                    <SectionTitle title="Reconciliation Logic" subtitle="Every validation formula is visible here." />
                    <div className="grid gap-3 lg:grid-cols-3">
                      <Metric label="Expected Closing" value={money(reconciliation.expected_closing_balance)} icon={FileText} tone="slate" />
                      <Metric label="Actual Closing" value={money(reconciliation.actual_closing_balance)} icon={Wallet} tone="slate" />
                      <Metric label="Ledger Difference" value={money(reconciliation.difference)} icon={ShieldCheck} tone={reconciliation.is_ledger_reconciled ? 'green' : 'rose'} />
                    </div>
                    <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50 p-4">
                      <p className="text-sm font-semibold text-slate-900">{cleanText(reconciliation.formula)}</p>
                      <div className="mt-3 grid gap-2 text-sm text-slate-700 md:grid-cols-2">
                        <div>Opening: {money(reconciliation.opening_balance)}</div>
                        <div>Period Credits: {money(reconciliation.period_credit)}</div>
                        <div>Period Debits: {money(reconciliation.period_debit)}</div>
                        <div>Trial Balance Difference: {money(reconciliation.trial_balance_difference)}</div>
                        <div>Balance Sheet Difference: {money(reconciliation.balance_sheet_difference)}</div>
                      </div>
                      <div className="mt-4 space-y-1 text-xs text-slate-500">
                        {(reconciliation.method_notes || []).map((note) => (
                          <p key={note}>{note}</p>
                        ))}
                      </div>
                    </div>
                  </section>
                ) : null}

                {activeTab === 'audit' ? (
                  <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                    <SectionTitle title="Audit Trail" subtitle="Creation and update metadata available from current ledger tables." />
                    <div className="overflow-x-auto">
                      <table className="min-w-[1200px] w-full text-sm">
                        <thead className="bg-slate-100 text-xs uppercase tracking-wide text-slate-600">
                          <tr>
                            <th className="px-3 py-3 text-left">Source</th>
                            <th className="px-3 py-3 text-left">User</th>
                            <th className="px-3 py-3 text-right">Amount</th>
                            <th className="px-3 py-3 text-left">Status</th>
                            <th className="px-3 py-3 text-left">Created</th>
                            <th className="px-3 py-3 text-left">Updated</th>
                            <th className="px-3 py-3 text-left">Remarks</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {auditTrail.map((audit) => (
                            <tr key={`${audit.source_table}-${audit.source_id}-${audit.user_name}`} className="hover:bg-slate-50">
                              <td className="px-3 py-3">
                                <div className="font-semibold">{audit.source_table} #{audit.source_id}</div>
                                <div className="text-xs text-slate-500">Txn #{audit.transaction_no}</div>
                              </td>
                              <td className="px-3 py-3">
                                <div>{cleanText(audit.user_name)}</div>
                                <div className="text-xs text-slate-500">{titleCase(audit.role)}</div>
                              </td>
                              <td className="px-3 py-3 text-right font-semibold">{money(audit.amount)}</td>
                              <td className="px-3 py-3">{titleCase(audit.status)}</td>
                              <td className="px-3 py-3">
                                <div>{cleanText(audit.created_by)}</div>
                                <div className="text-xs text-slate-500">{formatDate(audit.created_at)}</div>
                              </td>
                              <td className="px-3 py-3">
                                <div>{cleanText(audit.updated_by)}</div>
                                <div className="text-xs text-slate-500">{formatDate(audit.updated_at)}</div>
                              </td>
                              <td className="px-3 py-3 max-w-sm truncate" title={audit.remarks}>{cleanText(audit.remarks)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                ) : null}
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

const AccountingTable = ({ rows }) => (
  <div className="overflow-x-auto">
    <table className="min-w-[900px] w-full text-sm">
      <thead className="bg-slate-100 text-xs uppercase tracking-wide text-slate-600">
        <tr>
          <th className="px-3 py-3 text-left">Account</th>
          <th className="px-3 py-3 text-left">Group</th>
          <th className="px-3 py-3 text-right">Debit</th>
          <th className="px-3 py-3 text-right">Credit</th>
          <th className="px-3 py-3 text-right">Balance</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {rows.map((row) => (
          <tr key={row.account} className="hover:bg-slate-50">
            <td className="px-3 py-3 font-semibold text-slate-950">{cleanText(row.account)}</td>
            <td className="px-3 py-3">{cleanText(row.group)}</td>
            <td className="px-3 py-3 text-right text-sky-700">{money(row.debit)}</td>
            <td className="px-3 py-3 text-right text-emerald-700">{money(row.credit)}</td>
            <td className="px-3 py-3 text-right font-semibold">{money(row.balance)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const StatementList = ({ rows, emptyLabel }) => {
  if (!rows.length) {
    return <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-500">{emptyLabel}</div>;
  }

  return (
    <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
      {rows.map((row) => (
        <div key={row.account} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
          <span className="font-medium text-slate-800">{cleanText(row.account)}</span>
          <span className="font-semibold text-slate-950">{money(row.amount)}</span>
        </div>
      ))}
    </div>
  );
};

const SummaryTable = ({ rows, labelKey, labelTitle, onDrill }) => (
  <div className="overflow-x-auto">
    <table className="min-w-[900px] w-full text-sm">
      <thead className="bg-slate-100 text-xs uppercase tracking-wide text-slate-600">
        <tr>
          <th className="px-3 py-3 text-left">{labelTitle}</th>
          <th className="px-3 py-3 text-right">Credit</th>
          <th className="px-3 py-3 text-right">Debit</th>
          <th className="px-3 py-3 text-right">Net</th>
          <th className="px-3 py-3 text-right">Company Spend</th>
          <th className="px-3 py-3 text-right">Owner Spend</th>
          <th className="px-3 py-3 text-right">Admin Spend</th>
          <th className="px-3 py-3 text-right">Worker Spend</th>
          <th className="px-3 py-3 text-right">Admin Allocation</th>
          <th className="px-3 py-3 text-right">Worker Allocation</th>
          <th className="px-3 py-3 text-right">Details</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {rows.map((row) => (
          <tr key={row[labelKey]} className="hover:bg-slate-50">
            <td className="px-3 py-3 font-semibold text-slate-950">{cleanText(row[labelKey])}</td>
            <td className="px-3 py-3 text-right text-emerald-700">{money(row.credit)}</td>
            <td className="px-3 py-3 text-right text-rose-700">{money(row.debit)}</td>
            <td className="px-3 py-3 text-right font-semibold">{money(row.net)}</td>
            <td className="px-3 py-3 text-right font-semibold text-rose-700">{money(row.company_spending)}</td>
            <td className="px-3 py-3 text-right">{money(row.owner_spending)}</td>
            <td className="px-3 py-3 text-right">{money(row.admin_spending)}</td>
            <td className="px-3 py-3 text-right">{money(row.worker_spending)}</td>
            <td className="px-3 py-3 text-right">{money(row.admin_allocation)}</td>
            <td className="px-3 py-3 text-right">{money(row.worker_allocation)}</td>
            <td className="px-3 py-3 text-right">
              <button
                type="button"
                onClick={() => onDrill?.(row)}
                className="rounded-lg border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100"
              >
                View
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const ProjectSummaryTable = ({ rows, onDrill }) => (
  <div className="overflow-x-auto">
    <table className="min-w-[1100px] w-full text-sm">
      <thead className="bg-slate-100 text-xs uppercase tracking-wide text-slate-600">
        <tr>
          <th className="px-3 py-3 text-left">Project</th>
          <th className="px-3 py-3 text-right">Budget</th>
          <th className="px-3 py-3 text-right">Budget Credit</th>
          <th className="px-3 py-3 text-right">Expense Debit</th>
          <th className="px-3 py-3 text-right">Actual Spend</th>
          <th className="px-3 py-3 text-right">Remaining</th>
          <th className="px-3 py-3 text-right">Over Budget</th>
          <th className="px-3 py-3 text-right">Admin Spend</th>
          <th className="px-3 py-3 text-right">Worker Spend</th>
          <th className="px-3 py-3 text-right">Owner Spend</th>
          <th className="px-3 py-3 text-right">Admin Transfer</th>
          <th className="px-3 py-3 text-right">Worker Transfer</th>
          <th className="px-3 py-3 text-left">Heads</th>
          <th className="px-3 py-3 text-right">Details</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {rows.length === 0 ? (
          <tr>
            <td colSpan={14} className="px-3 py-8 text-center text-slate-500">No project budget data found.</td>
          </tr>
        ) : rows.map((row) => (
          <tr key={row.project_name} className={Number(row.over_budget || 0) > 0 ? 'bg-rose-50/60' : 'hover:bg-slate-50'}>
            <td className="px-3 py-3 font-semibold text-slate-950">{cleanText(row.project_name)}</td>
            <td className="px-3 py-3 text-right">{money(row.allocated_budget)}</td>
            <td className="px-3 py-3 text-right text-emerald-700">{money(row.project_credit)}</td>
            <td className="px-3 py-3 text-right text-rose-700">{money(row.project_debit)}</td>
            <td className="px-3 py-3 text-right font-semibold text-rose-700">{money(row.actual_spend)}</td>
            <td className={`px-3 py-3 text-right font-semibold ${Number(row.remaining_budget || 0) < 0 ? 'text-rose-700' : 'text-emerald-700'}`}>{money(row.remaining_budget)}</td>
            <td className="px-3 py-3 text-right font-semibold text-rose-700">{Number(row.over_budget || 0) > 0 ? money(row.over_budget) : '-'}</td>
            <td className="px-3 py-3 text-right">{money(row.admin_spending)}</td>
            <td className="px-3 py-3 text-right">{money(row.worker_spending)}</td>
            <td className="px-3 py-3 text-right">{money(row.owner_spending)}</td>
            <td className="px-3 py-3 text-right text-slate-500">{money(row.allocated_to_admin)}</td>
            <td className="px-3 py-3 text-right text-slate-500">{money(row.allocated_to_worker)}</td>
            <td className="px-3 py-3 text-xs text-slate-500 max-w-[240px]">{(row.heads || []).join(', ') || '-'}</td>
            <td className="px-3 py-3 text-right">
              <button
                type="button"
                onClick={() => onDrill?.(row)}
                className="rounded-lg border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100"
              >
                View
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const ProjectBudgetCard = ({ project, onOpen }) => {
  const budget = Number(project.allocated_budget || 0);
  const credit = Number(project.project_credit || 0);
  const debit = Number(project.project_debit || 0);
  const spent = Number(project.actual_spend || 0);
  const safeBudget = budget > 0 ? budget : Math.max(credit, debit, spent, 1);
  const usedPct = Math.min(100, (spent / safeBudget) * 100);
  const circumference = 2 * Math.PI * 42;
  const dash = (usedPct / 100) * circumference;
  const overBudget = Number(project.over_budget || 0) > 0;
  const splitTotal = Math.max(Number(project.admin_spending || 0) + Number(project.worker_spending || 0) + Number(project.owner_spending || 0), 1);
  const adminPct = Math.min(100, (Number(project.admin_spending || 0) / splitTotal) * 100);
  const workerPct = Math.min(100, (Number(project.worker_spending || 0) / splitTotal) * 100);

  return (
    <button
      type="button"
      onClick={onOpen}
      className="text-left rounded-xl border border-slate-200 bg-slate-50 p-4 hover:border-sky-300 hover:bg-white transition"
    >
      <div className="flex items-start gap-4">
        <svg width="108" height="108" viewBox="0 0 108 108" className="shrink-0">
          <circle cx="54" cy="54" r="42" fill="none" stroke="#e2e8f0" strokeWidth="12" />
          <circle
            cx="54"
            cy="54"
            r="42"
            fill="none"
            stroke={overBudget ? '#e11d48' : '#0284c7'}
            strokeWidth="12"
            strokeDasharray={`${dash} ${circumference - dash}`}
            strokeLinecap="round"
            transform="rotate(-90 54 54)"
          />
          <text x="54" y="50" textAnchor="middle" className="fill-slate-900 text-[16px] font-bold">
            {Math.round(usedPct)}%
          </text>
          <text x="54" y="66" textAnchor="middle" className="fill-slate-500 text-[9px] font-semibold">
            used
          </text>
        </svg>
        <div className="min-w-0 flex-1">
          <div className="font-bold text-slate-950 truncate">{cleanText(project.project_name)}</div>
          <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
            <div>
              <div className="text-slate-500">Budget</div>
              <div className="font-semibold text-slate-900">{money(project.allocated_budget)}</div>
            </div>
            <div>
              <div className="text-slate-500">Spent</div>
              <div className="font-semibold text-rose-700">{money(project.actual_spend)}</div>
            </div>
            <div>
              <div className="text-slate-500">Budget Credit</div>
              <div className="font-semibold text-emerald-700">{money(project.project_credit)}</div>
            </div>
            <div>
              <div className="text-slate-500">Expense Debit</div>
              <div className="font-semibold text-rose-700">{money(project.project_debit)}</div>
            </div>
            <div>
              <div className="text-slate-500">Remaining</div>
              <div className={Number(project.remaining_budget || 0) < 0 ? 'font-semibold text-rose-700' : 'font-semibold text-emerald-700'}>
                {money(project.remaining_budget)}
              </div>
            </div>
            <div>
              <div className="text-slate-500">Transactions</div>
              <div className="font-semibold text-slate-900">{Number(project.transaction_count || 0)}</div>
            </div>
          </div>
          <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-200">
            <div className="h-full bg-sky-600" style={{ width: `${adminPct}%`, float: 'left' }} />
            <div className="h-full bg-amber-500" style={{ width: `${workerPct}%`, float: 'left' }} />
          </div>
          <div className="mt-2 text-xs text-slate-500">
            Admin {money(project.admin_spending)} / Worker {money(project.worker_spending)} / Owner {money(project.owner_spending)}
          </div>
        </div>
      </div>
    </button>
  );
};

const ProjectComparisonChart = ({ projects, onOpen }) => {
  const chartRows = [...projects]
    .filter((project) => cleanText(project.project_name, '') !== '')
    .sort((a, b) => Number(b.allocated_budget || 0) - Number(a.allocated_budget || 0))
    .slice(0, 8);

  if (!chartRows.length) {
    return null;
  }

  const maxAmount = Math.max(
    ...chartRows.flatMap((project) => [
      Number(project.allocated_budget || 0),
      Number(project.project_credit || 0),
      Number(project.allocated_to_admin || 0),
      Number(project.allocated_to_worker || 0),
      Number(project.actual_spend || 0),
    ]),
    1
  );

  const widthFor = (value) => `${Math.max(2, Math.min(100, (Number(value || 0) / maxAmount) * 100))}%`;

  return (
    <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-bold text-slate-950">Project Wise Bar Chart</h3>
          <p className="text-xs text-slate-500">Transfers are separate from budget usage; remaining budget follows actual spend only.</p>
        </div>
        <div className="flex flex-wrap gap-2 text-[11px] font-semibold text-slate-600">
          <span className="inline-flex items-center gap-1"><i className="h-2.5 w-2.5 rounded-sm bg-slate-700" /> Budget</span>
          <span className="inline-flex items-center gap-1"><i className="h-2.5 w-2.5 rounded-sm bg-emerald-600" /> Budget Credit</span>
          <span className="inline-flex items-center gap-1"><i className="h-2.5 w-2.5 rounded-sm bg-violet-600" /> Admin Transfer</span>
          <span className="inline-flex items-center gap-1"><i className="h-2.5 w-2.5 rounded-sm bg-amber-500" /> Worker Transfer</span>
          <span className="inline-flex items-center gap-1"><i className="h-2.5 w-2.5 rounded-sm bg-sky-600" /> Actual Spend</span>
        </div>
      </div>
      <div className="space-y-3">
        {chartRows.map((project) => (
          <button
            key={project.project_name}
            type="button"
            onClick={() => onOpen?.(project)}
            className="grid w-full gap-2 rounded-lg border border-transparent p-2 text-left transition hover:border-sky-200 hover:bg-white lg:grid-cols-[170px_1fr]"
          >
            <div className="min-w-0">
              <p className="truncate text-sm font-bold text-slate-900">{cleanText(project.project_name)}</p>
              <p className="text-xs text-slate-500">Remaining {money(project.remaining_budget)}</p>
            </div>
            <div className="space-y-1.5">
              {[
                ['Budget', project.allocated_budget, 'bg-slate-700'],
                ['Credit', project.project_credit, 'bg-emerald-600'],
                ['Admin', project.allocated_to_admin, 'bg-violet-600'],
                ['Worker', project.allocated_to_worker, 'bg-amber-500'],
                ['Spend', project.actual_spend, 'bg-sky-600'],
              ].map(([label, value, color]) => (
                <div key={label} className="grid grid-cols-[52px_1fr_96px] items-center gap-2 text-[11px]">
                  <span className="font-semibold text-slate-500">{label}</span>
                  <span className="h-2.5 overflow-hidden rounded-full bg-slate-200">
                    <span className={`block h-full rounded-full ${color}`} style={{ width: widthFor(value) }} />
                  </span>
                  <span className="text-right font-semibold text-slate-700">{shortMoney(value)}</span>
                </div>
              ))}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default LedgerIntelligenceReport;
